<template>
  <div id="metadata">
    <div v-if="canChangeSpaceZone" class="item">
      <v-label class="row">
        <span>{{ t("region") }}:</span>

        <v-select
          v-model="selectedRegion"
          @change="applyRegionChange"
          :items="organizationSupportedRegions"
          :loading="loader.on"
          rbt-target="regionPicker"
          hide-details
        >
          <template v-slot:item="{ item }">
            <span :rbt-target="toCamelCase(item.value)">
              {{ item.text }}
            </span>
          </template>
        </v-select>
      </v-label>
    </div>

    <div v-if="canChangeSpaceZone" class="item item__divider-right">
      <v-label class="row">
        <span>{{ t("zone") }}:</span>

        <v-select
          v-model="selectedZone"
          @change="applyRegionChange"
          :items="regionZonesOptions"
          :loading="loader.on"
          :disabled="!selectedRegion"
          :key="selectedRegion"
          rbt-target="zonePicker"
          hide-details
        >
          <template v-slot:item="{ item }">
            <span :rbt-target="toCamelCase(item.value)">
              {{ item.text }}
            </span>
          </template>
        </v-select>
      </v-label>
    </div>

    <div v-if="cloudZone && isDeployed" class="item">
      <span class="prefix">{{ t("region") }}:</span>

      <tooltip :text="`CloudZoneAZ: ${cloudZone.az}`">
        <span class="value">
          {{ cloudZone.name }}
        </span>

        <span class="prefix">
          <v-icon :size="12">mdi-chevron-double-right</v-icon>
        </span>

        <span class="value">
          {{ cloudZone.code }}
        </span>
      </tooltip>
    </div>

    <div v-if="workspace.stateChange" class="item">
      <span class="prefix">{{ t("lastEdited") }}:</span>

      <tooltip :text="lastChangedDate">
        <span class="lastEdited">
          <span class="value templateName">{{ lastEdited }}</span>
          <span class="prefix">{{ t("lastEditedBy") }}</span>
          <span class="value templateName">{{ workspace.stateChange.username }}</span>
        </span>
      </tooltip>
    </div>

    <div v-if="isOnline && isDeployed" class="item">
      <span class="prefix">{{ t("runningSince") }}:</span>

      <tooltip :text="t('uptime')">
        <running-for :running-since="runningSince" />
      </tooltip>
    </div>

    <div class="item">
      <space-capacity :workspace="workspace" />
    </div>

    <div class="item">
      <space-details :space="workspace" />
    </div>
  </div>
</template>

<script lang="ts">
import { WorkspaceTemplate, WorkspaceOnlineStatusEnum, WorkspaceStatusEnum } from "@/types/workspace"
import { ref, computed, defineComponent, nextTick, PropType } from "@vue/composition-api"
import { useStore } from "@/composable/useStore"
import { WorkspaceStates } from "@/config/workspace-states"
import { createTranslationModule, translate } from "@/plugins/i18n"
import { Dictionary } from "vue-router/types/router"
import { CloudZone, SelectItem } from "@/types/core"
import Tooltip from "@/components/tooltip/TooltipRoot.vue"
import RunningFor from "../shared/RunningFor.vue"
import Utils from "@/utils/utils"
import Loader from "@/utils/loader"
import SpaceDetails from "./SpaceDetails.vue"
import SpaceCapacity from "./_SpaceCapacity.vue"
import { raiseWarning } from "@/utils/event-bus"

export default defineComponent({
  name: "Metadata",
  props: {
    workspace: {
      type: Object as PropType<WorkspaceTemplate>,
      required: true
    }
  },
  setup(props) {
    const { spaceModule, usersModule, organizationModule } = useStore()
    const t = createTranslationModule("WorkspaceEdit.metadata")
    const selectedRegion = ref<string | null>("")
    const selectedZone = ref<string | null>("")
    const loader = Loader()

    const organizationSupportedRegions = computed(() => {
      return organizationModule.organizationDetail.cloudZones.reduce(
        (selectItemCollection: SelectItem<string | null>[], zone) => {
          const region = spaceModule.spaceRegions.find(region => region.code === zone)

          if (region) {
            selectItemCollection.push({
              text: region.fullName,
              value: zone
            })
          }

          return selectItemCollection
        },
        [
          {
            text: translate("WorkspaceEdit.auto"),
            value: null
          }
        ]
      )
    })

    const regionZonesOptions = computed(() => {
      if (selectedRegion.value) {
        return spaceModule.spaceRegions.reduce(
          (selectItemCollection: SelectItem<string | null>[], zone: CloudZone) => {
            if (zone.code === selectedRegion.value) {
              selectItemCollection.push(
                ...zone.zones.map(z => ({
                  text: z,
                  value: z
                }))
              )
            }

            return selectItemCollection
          },
          [
            {
              text: translate("WorkspaceEdit.auto"),
              value: null
            }
          ]
        )
      } else {
        return [
          {
            text: translate("WorkspaceEdit.auto"),
            value: null
          }
        ]
      }
    })

    const canChangeSpaceZone = computed(() => {
      const isDraft = props.workspace.status === WorkspaceStatusEnum.Draft

      if (isDraft) {
        return usersModule.hasEditorPermissions
      }

      return false
    })

    const cloudZone = computed(() => {
      if (props.workspace.cloudZone && props.workspace.cloudZoneAZ) {
        const regions = spaceModule.spaceRegions
        const regionDataFromCode = regions.find(region => region.code === props.workspace.cloudZone)

        if (regionDataFromCode) {
          return {
            name: regionDataFromCode.fullName,
            code: props.workspace.cloudZone,
            az: props.workspace.cloudZoneAZ
          }
        }
      }

      return null
    })

    const lastEdited = computed(() => {
      const stateChange = props.workspace.stateChange

      if (stateChange) {
        const { changeDate } = stateChange
        const _time = Utils.lastModified(changeDate)

        if (_time.includes("sec")) {
          return translate("Modified.fewSecondsAgo")
        }

        return _time
      }

      return null
    })

    const lastChangedDate = computed(() => {
      if (props.workspace.stateChange) {
        const { ...onlineStatus } = WorkspaceOnlineStatusEnum as Dictionary<string>
        const date = Utils.dateFormatWithTime(props.workspace.stateChange.changeDate)
        const stateChangeStatus = props.workspace.stateChange.changedTo
        const wasOnlineStatus = Object.values(onlineStatus).includes(stateChangeStatus)
        const snippetCode = wasOnlineStatus ? "workspaceOnlineStatus" : "workspaceDisplayStatus"

        return translate(`${snippetCode}.${stateChangeStatus}`) + ` - ${date}`
      }

      return ""
    })

    const isOnline = computed(() => {
      const { Online } = WorkspaceOnlineStatusEnum
      return props.workspace.onlineStatus === Online
    })

    const isDeployed = computed(() => {
      return WorkspaceStates.isDeployed(props.workspace.status)
    })

    const runningSince = computed(() => {
      return props.workspace.runningSince
    })

    const toCamelCase = (word: string) => {
      const regionCode = `${word}`.split("-").join(" ")
      return Utils.toCamel("region option " + regionCode)
    }

    const applyRegionChange = () => {
      nextTick(() => {
        loader.run(async () => {
          if (!_isValidRegionZone(selectedRegion.value)) {
            selectedZone.value = null
          }

          await spaceModule.changeRegion(props.workspace.id, selectedRegion.value, selectedZone.value)

          raiseWarning({
            text: t("zoneModified"),
            timeout: 3000
          })
        })
      })
    }

    const _isValidRegionZone = (regionCode: string | null) => {
      if (regionCode) {
        const region = spaceModule.spaceRegions.find(region => region.code === regionCode)

        if (region && selectedZone.value) {
          return region.zones.includes(selectedZone.value)
        }
      }

      return false
    }

    const _updatePreDefinedCloudZone = () => {
      selectedRegion.value = props.workspace.cloudZone
      selectedZone.value = props.workspace.cloudZoneAZ
    }
    _updatePreDefinedCloudZone()

    return {
      t,
      regionZonesOptions,
      toCamelCase,
      applyRegionChange,
      loader,
      canChangeSpaceZone,
      selectedRegion,
      organizationSupportedRegions,
      cloudZone,
      lastChangedDate,
      lastEdited,
      isDeployed,
      isOnline,
      runningSince,
      selectedZone
    }
  },
  components: {
    SpaceCapacity,
    SpaceDetails,
    Tooltip,
    RunningFor
  }
})
</script>
